/**
 * This set of hooks is how we'll interact with the groupStore. The idea is to provide a simple api to get what
 * we need from the store without having to use `dispatch`, `connect`, `mapStoreToProps`, and all that stuff
 * in the components.
 */

import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux'
import apiUtils from '../../global/utils/api';
import { createEndpoint, parseQueryArgs, selectSingleById } from '../../global/utils/storeUtils';

// import all of the actions from the store
import {
  fetchGroupList
  , fetchGroupListAtEndpoint
  , fetchListIfNeeded
  , fetchSingleGroup
  , fetchSingleGroupAtEndpoint
  , sendCreateGroup
  , sendUpdateGroup
  , sendDeleteGroup
  , sendCheckSecureGroupLanding
  , invalidateQuery
  // , invalidateQueries
  , addGroupToList
  , fetchSingleIfNeeded
  , myGroupsEndpoint
} from './groupStore';
import {
  useGetResourceById
  , useGetResource
  , useGetResourceList
  , useMutateResource
} from '../../global/utils/serviceHooks';


// Define the hooks that we'll use to manage data in the components

// CREATE

/**
 * Use this hook to handle the creation of a new group.
 * @param {Object} initialState - The initial state of the group (optional)
 * @param {Function} handleResponse - The function to call when the group is successfully created
 * 
 * @returns an object containing fetch info and the following:
 * - `newGroup` as `data`: the new group object as it currently exists in state, initially the default group
 * - `handleChange`: standard form change handler to be used in the form
 * - `handleSubmit`: standard form submit handler to be used in the form
 * - `setFormState`: a way to handle form state changes in the component instead of `handleChange`, rarely needed but sometimes necessary
 * @example // to use in a component
 * // access the create action and fetch the default group
 * const { data: newGroup, handleChange, handleSubmit, ...groupQuery } = useCreateGroup({
 *   // optional, anything we want to add to the default object
 *   initialState: {
 *     someKey: 'someValue'
 *   }
 *   // optional, callback function that receives the response from the server
 *   , handleResponse: (group, error) => {
 *     if(error || !group) {
 *       alert(error || "An error occurred.")
 *     }
 *     history.push(`/groups/${group._id}`)
 *   }
 * });
 * 
 * return (
 *   <WaitOn query={groupQuery}>
 *     <GroupForm
 *       group={group}
 *       handleSubmit={handleSubmit}
 *       handleChange={handleChange}
 *     />
 *   </WaitOn>
 * )
 */
export const useCreateGroup = ({ initialState = {}, onResponse = () => { } }) => {
  const dispatch = useDispatch();
  // set up group specific stuff to be used by the shared hook
  const defaultGroupQuery = useGetDefaultGroup();
  const sendMutation = (mutatedGroup) => dispatch(sendCreateGroup(mutatedGroup));

  // the hook will return everything the caller needs to create a new group
  return useMutateResource({ resourceQuery: defaultGroupQuery, sendMutation, initialState, onResponse, isCreate: true });
}

// READ

/**
 * NOTE: If you are using this because you want to create a new group, try `useCreateGroup`
 * instead. It handles everything for you!
 * 
 * @param {boolean} forceFetch - optional override to force a fetch from the server
 * @returns an object containing fetch info and eventually the defaultGroup (as `data`)
 * @returns a refetch function for convenience (will probably never be used for default group, but keeps things consistent)
 */
export const useGetDefaultGroup = (forceFetch = false) => {
  // leverage existing hooks to get the default group (using 'default' as the id will return the default group from the server)
  // we want to add a flag on the default group to create default roles on creation
  // normally we'd have a default on the model, but this is just a flag that isn't persisted, but used to trigger a server side action
  const { data: defaultGroup = {}, ...defaultGroupQuery } = useGetGroupById('default', forceFetch);
  const updatedDefaultGroup = { ...defaultGroup, createDefaultRoles: true };
  // return the default group with the flag and the rest of the query info
  return { data: updatedDefaultGroup, ...defaultGroupQuery };
}


/**
 * This hook will check for a fresh group in the store and fetch a new one if necessary
 * 
 * @param {string} id - the id of the group to be fetched
 * @param {boolean} forceFetch - optional override to force a fetch from the server
 * @returns an object containing fetch info and eventually the group (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetGroupById = (id, forceFetch = false) => {
  const dispatch = useDispatch();
  // set up group specific stuff to be used by the shared hook
  const groupStore = useSelector(({ group }) => group);
  const fetchGroup = forceFetch ? fetchSingleGroup : fetchSingleIfNeeded;
  const sendFetchById = (id) => dispatch(fetchGroup(id));
  const sendInvalidateSingle = (id) => dispatch(invalidateQuery(id));

  // return the (now group specific) hook
  return useGetResourceById({ id, fromStore: groupStore, sendFetchById, sendInvalidateSingle });
}

/**
 * This hook will check for a fresh group in the store and fetch a new one if necessary
 * 
 * @param {...string | object | null} args - accepts two optional arguments: a string (endpoint) or an object (listArgs) or both as (endpoint, listArgs)
 * @returns an object containing fetch info and eventually the group (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetGroup = (...args) => {
  const dispatch = useDispatch();
  const { endpoint, listArgs } = parseQueryArgs(args);
  // set up group specific stuff to be used by the shared hook
  const groupStore = useSelector(({ group }) => group);
  // if an endpoint was passed in, use that action, otherwise use the default
  const fetchAction = endpoint ? fetchSingleGroupAtEndpoint : fetchSingleGroup;
  // finally package up the actions to be used by the shared hook
  const sendFetchSingle = (queryString) => dispatch(fetchSingleIfNeeded(queryString, fetchAction));
  const sendInvalidateSingle = (queryString) => dispatch(invalidateQuery(queryString));
  // return the (now group specific) hook
  return useGetResource({ listArgs, fromStore: groupStore, sendFetchSingle, sendInvalidateSingle, endpoint });
}

/**
 * This hook will check for a fresh list in the store and fetch a new one if necessary
 * 
 * @param {...string | object | null} args - accepts two optional arguments: a string (endpoint) or an object (listArgs) or both as (endpoint, listArgs)
 * @returns an object containing fetch info and eventually the group list (as `data`)
 * @returns an invalidate and refetch function for convenience
 */
export const useGetGroupList = (...args) => {
  const { endpoint, listArgs } = parseQueryArgs(args);
  const dispatch = useDispatch();
  // set up group specific stuff to be used by the shared hook
  const groupStore = useSelector(({ group }) => group);
  // if an endpoint was passed in, use that action, otherwise use the default
  const fetchAction = endpoint ? fetchGroupListAtEndpoint : fetchGroupList;
  // finally package up the actions to be used by the shared hook
  const sendFetchList = (queryString) => dispatch(fetchListIfNeeded(queryString, fetchAction));
  const sendInvalidateList = (queryString) => dispatch(invalidateQuery(queryString));

  // return the (now group specific) hook
  return useGetResourceList({ listArgs, fromStore: groupStore, sendFetchList, sendInvalidateList, endpoint });
}

// UPDATE

/**
 * Use this hook to access the `sendUpdateGroup` action
 * 
 * Useful if you want to update a group that you already have access to
 * 
 * NOTE: Check out `useGetUpdatableGroup` if you want to fetch and update a group
 * 
 * @returns the sendUpdateGroup action wrapped in dispatch
 * @example // to use in a component
 * // access the update action
 * const { sendUpdateGroup } = useUpdateGroup();
 * // dispatch the update action
 * sendUpdateGroup(updatedGroup);
 */
export const useUpdateGroup = () => {
  const dispatch = useDispatch();
  return {
    // return the update action
    sendUpdateGroup: (updatedGroup) => dispatch(sendUpdateGroup(updatedGroup))
  }
}

/**
 * Use this hook to handle the update of an existing group.
 * @param {string} id - the id of the group to be updated.
 * @param {Object} options - an object that expects an optional onResponse function that receives the updated group and error.
 * 
 * @returns an object containing fetch info and the following:
 * - `group` as `data`: the group object as it currently exists in state
 * - `isChanged`: a boolean that is true if the group has pending changes that have not been saved
 * - `handleChange`: standard form change handler to be used in the form
 * - `handleSubmit`: standard form submit handler to be used in the form
 * - `setFormState`: a way to handle form state changes in the component instead of `handleChange`, rarely needed but sometimes necessary
 * @example // to use in a component
 * // fetch the group and access everything needed to handle updating it
 * const { data: group, handleChange, handleSubmit, ...groupQuery } = useGetUpdatableGroup(groupId, {
 *   // optional, callback function to run after the request is complete
 *   onResponse: (updatedGroup, error) => {
 *     if(error || !updatedGroup) {
 *       alert(error || "An error occurred.")
 *     }
 *     history.push(`/groups/${groupId}`)
 *   }
 * });
 * 
 * return (
 *   <WaitOn query={groupQuery}>
 *     <GroupForm
 *       group={group}
 *       handleSubmit={handleSubmit}
 *       handleChange={handleChange}
 *     />
 *   </WaitOn>
 * )
 */

export const useAdminUpdateGroup = (id, { onResponse = () => { } } = {}) => {
  const dispatch = useDispatch();
  // set up group specific stuff to be used by the shared hook
  // use the existing hook to get the groupQuery
  const groupQuery = useGetGroupById(id);
  const sendMutation = (mutatedGroup) => dispatch(sendUpdateGroup({ _id: id, ...mutatedGroup }));
  // wrap onResponse to destructure the group from the combined {group, organization} payload
  const wrappedOnResponse = (payload, err) => {
    const { group } = payload;
    onResponse(group, err);
  }
  // return the (now group specific) hook
  return useMutateResource({ resourceQuery: groupQuery, sendMutation, onResponse: wrappedOnResponse, id });
}

export const useGetUpdatableGroup = (id, { onResponse = () => { }, endpoint } = {}) => {
  const dispatch = useDispatch();
  // set up group specific stuff to be used by the shared hook
  // use the existing hook to get the groupQuery
  const groupQuery = useGetGroup(myGroupsEndpoint, { _id: id });
  const sendMutation = (mutatedGroup) => dispatch(sendUpdateGroup({ endpoint, method: 'PUT', _id: id, ...mutatedGroup }));
  // wrap onResponse to destructure the group from the combined {group, organization} payload
  const wrappedOnResponse = (payload, err) => {
    const { group } = payload;
    onResponse(group, err);
  }
  // return the (now group specific) hook
  return useMutateResource({ resourceQuery: groupQuery, sendMutation, onResponse: wrappedOnResponse, id });
}

// unused for now, but when we want to allow them to change stuff for their group, we can use this
const getGroupAdminUpdateEndpoint = createEndpoint(`group-admin-update/:groupId`);
export const useGroupAdminUpdateGroup = (groupId, { onResponse = () => { } } = {}) => {
  const endpoint = getGroupAdminUpdateEndpoint(groupId);
  return useGetUpdatableGroup(groupId, { onResponse, endpoint });
}


// DELETE

/**
 * Use this hook to access the `sendDeleteGroup` action
 * 
 * @returns the sendDeleteGroup action wrapped in dispatch
 * 
 * @example // to use in a component
 * // access the delete action
 * const { sendDeleteGroup } = useDeleteGroup();
 * // dispatch the delete action
 * sendDeleteGroup(groupId);
 */
export const useDeleteGroup = () => {
  const dispatch = useDispatch();
  return {
    // return the delete action
    sendDeleteGroup: (id) => dispatch(sendDeleteGroup(id))
  }
}

// SPECIAL
export const useSecureGroupLanding = (...args) => {
  const { listArgs } = parseQueryArgs(args);
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [redirect, setRedirect] = useState(null);
  const [orgId, setOrgId] = useState(null);

  const readyToSend = apiUtils.checkListArgsReady(listArgs);
  const queryString = apiUtils.queryStringFromObject(listArgs);

  const handleSend = async () => {
    if(!readyToSend) return;
    if(isLoading) return;
    if(message || redirect) return;
    setIsLoading(true);
    const { payload, error} = await dispatch(sendCheckSecureGroupLanding(`?${queryString}`))
    if(error) {
      setIsLoading(false);
      setMessage(error.message);
    }
    if(payload?.redirect) {
      setRedirect(payload.redirect);
    }
    if(payload?.organizationId) {
      setOrgId(payload.organizationId);
    }
  };

  useEffect(() => {
    handleSend();
  }, [handleSend, readyToSend]);

  return { message, redirect, organizationId: orgId, isLoading };
}

const getAllReportsEndpoint = createEndpoint(`insights/all-reports`);

export const useGetAllStandardReports = () => {
  const { data: none, otherData: { reportsMap = {} } = {}, ...allReportsQuery } = useGetGroup(getAllReportsEndpoint());
  const reportsAsArray = Object.keys(reportsMap).map(key => reportsMap[key]);
  return { reportsMap, data: reportsAsArray, ...allReportsQuery };
};

const getMyGroupReportsEndpoint = createEndpoint(`:groupId/insights`);
export const useGetMyGroupReports = (groupId) => {
  const { otherData: { reports = [] } = {}, ...myGroupReportsQuery } = useGetGroup(getMyGroupReportsEndpoint({groupId}));
  return { reports, ...myGroupReportsQuery };
};



// OTHERS

/**
 * @returns the `addGroupToList` action wrapped in dispatch
 */
export const useAddGroupToList = () => {
  const dispatch = useDispatch();
  return {
    addGroupToList: (groupId, listArgs) => dispatch(addGroupToList({ id: groupId, queryKey: apiUtils.queryStringFromObject(listArgs) || "all" }))
  }
}

/**
 * NOTE: Only use this if you're sure the group is already in the store. WILL NOT fetch from the server.
 * @param {string} id - the id of the group that you want to grab from the store
 * @returns the group from the store's byId map
 */
export const useGroupFromMap = (id) => {
  const group = useSelector(({ group: groupStore }) => selectSingleById(groupStore, id));
  return group
}
